import { useState, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Download, Eye, Check, X, Clock, Send,
  Truck, Home, MapPin, User, Phone, Mail, Calendar,
  Euro, Package, ChevronDown, ChevronUp, Search, Filter
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Layout from '@/components/Layout';

const AdminQuotes = ({ user }) => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [filter, setFilter] = useState({ type: '', status: '' });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch quotes
  useEffect(() => {
    fetchQuotes();
  }, [filter]);

  const fetchQuotes = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.type) params.append('quote_type', filter.type);
      if (filter.status) params.append('status', filter.status);
      
      const response = await axios.get(`${API}/quotes?${params.toString()}`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update quote status
  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}`, { status });
      fetchQuotes();
      if (selectedQuote?.id === quoteId) {
        setSelectedQuote({ ...selectedQuote, status });
      }
    } catch (error) {
      console.error('Error updating quote:', error);
      toast.error(`Statut du devis non enregistré : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  // Update final price
  const updateFinalPrice = async (quoteId, finalPrice) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}`, { final_price: parseFloat(finalPrice) });
      fetchQuotes();
    } catch (error) {
      console.error('Error updating price:', error);
      toast.error(`Prix non enregistré : ${typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'vérifiez la connexion puis réessayez'}`);
    }
  };

  // Download PDF
  const downloadPDF = async (quoteId) => {
    try {
      const res = await axios.get(`${API}/quotes/${quoteId}/pdf`, { responseType: 'blob', withCredentials: true });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `devis-${quoteId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('PDF du devis indisponible : vérifiez la connexion puis réessayez');
    }
  };

  // Filter quotes
  const filteredQuotes = quotes.filter(quote => {
    if (searchTerm) {
      const client = quote.quote_data?.client || {};
      const searchLower = searchTerm.toLowerCase();
      return (
        client.name?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower) ||
        client.phone?.includes(searchTerm)
      );
    }
    return true;
  });

  // Status badge
  const StatusBadge = ({ status }) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      validated: 'bg-blue-100 text-blue-700',
      sent: 'bg-purple-100 text-purple-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const labels = {
      pending: 'En attente',
      validated: 'Validé',
      sent: 'Envoyé',
      accepted: 'Accepté',
      rejected: 'Refusé'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <Layout user={user} activeModule="b2b">
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Gestion des Devis</h1>
          <p className="text-slate-600">Gérez les demandes de devis des clients</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, email, téléphone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              className="px-3 py-2 border rounded-lg"
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            >
              <option value="">Tous les types</option>
              <option value="delivery">Livraison</option>
              <option value="moving">Déménagement</option>
            </select>
            <select
              className="px-3 py-2 border rounded-lg"
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="validated">Validé</option>
              <option value="sent">Envoyé</option>
              <option value="accepted">Accepté</option>
              <option value="rejected">Refusé</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-slate-900">{quotes.length}</div>
            <div className="text-sm text-slate-600">Total</div>
          </div>
          <div className="bg-yellow-50 rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-yellow-700">
              {quotes.filter(q => q.status === 'pending').length}
            </div>
            <div className="text-sm text-yellow-600">En attente</div>
          </div>
          <div className="bg-blue-50 rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-blue-700">
              {quotes.filter(q => q.quote_type === 'delivery').length}
            </div>
            <div className="text-sm text-blue-600">Livraisons</div>
          </div>
          <div className="bg-orange-50 rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-orange-700">
              {quotes.filter(q => q.quote_type === 'moving').length}
            </div>
            <div className="text-sm text-orange-600">Déménagements</div>
          </div>
        </div>

        {/* Quotes List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Chargement...</div>
          ) : filteredQuotes.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Aucun devis trouvé</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Distance</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Véhicule</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Prix estimé</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredQuotes.map(quote => {
                    const client = quote.quote_data?.client || {};
                    return (
                      <tr key={quote.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                            quote.quote_type === 'delivery' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            {quote.quote_type === 'delivery' ? <Package className="w-3 h-3" /> : <Home className="w-3 h-3" />}
                            {quote.quote_type === 'delivery' ? 'Livraison' : 'Déménagement'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{client.name || 'N/A'}</div>
                          <div className="text-sm text-slate-500">{client.email || ''}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-900">{quote.distance_km || '-'} km</div>
                          <div className="text-sm text-slate-500">{quote.estimated_duration || ''}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{quote.suggested_vehicle || '-'}</td>
                        <td className="px-4 py-3">
                          {quote.final_price ? (
                            <div className="font-semibold text-green-600">{quote.final_price}€</div>
                          ) : quote.estimated_price ? (
                            <div className="text-slate-600">~{quote.estimated_price}€</div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={quote.status} />
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedQuote(quote)}
                              className="text-slate-600"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadPDF(quote.id)}
                              className="text-blue-600"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {quote.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateQuoteStatus(quote.id, 'validated')}
                                className="text-green-600"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quote Detail Modal */}
        {selectedQuote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedQuote(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Détails du devis</h2>
                  <p className="text-sm text-slate-500">#{selectedQuote.id.slice(0, 8).toUpperCase()}</p>
                </div>
                <Button variant="ghost" onClick={() => setSelectedQuote(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Client Info */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Client
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Nom:</span>
                      <p className="font-medium">{selectedQuote.quote_data?.client?.name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Téléphone:</span>
                      <p className="font-medium">{selectedQuote.quote_data?.client?.phone}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Email:</span>
                      <p className="font-medium">{selectedQuote.quote_data?.client?.email}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Date souhaitée:</span>
                      <p className="font-medium">{selectedQuote.quote_data?.client?.preferred_date || 'Non spécifiée'}</p>
                    </div>
                  </div>
                </div>

                {/* Addresses */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Adresses
                  </h3>
                  <div className="space-y-3 text-sm">
                    {selectedQuote.quote_type === 'delivery' ? (
                      <>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <span className="text-slate-500">Prise en charge:</span>
                          <p className="font-medium">{selectedQuote.quote_data?.pickup_address?.address}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <span className="text-slate-500">Livraison:</span>
                          <p className="font-medium">{selectedQuote.quote_data?.delivery_address?.address}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <span className="text-slate-500">Départ:</span>
                          <p className="font-medium">{selectedQuote.quote_data?.departure_address?.address}</p>
                          <p className="text-xs text-slate-500">
                            {selectedQuote.quote_data?.departure_location_type} | 
                            Étage {selectedQuote.quote_data?.departure_floor || 'RDC'} | 
                            Ascenseur: {selectedQuote.quote_data?.departure_elevator ? 'Oui' : 'Non'}
                          </p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <span className="text-slate-500">Arrivée:</span>
                          <p className="font-medium">{selectedQuote.quote_data?.arrival_address?.address}</p>
                          <p className="text-xs text-slate-500">
                            {selectedQuote.quote_data?.arrival_location_type} | 
                            Étage {selectedQuote.quote_data?.arrival_floor || 'RDC'} | 
                            Ascenseur: {selectedQuote.quote_data?.arrival_elevator ? 'Oui' : 'Non'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Distance & Vehicle */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-xl text-center">
                    <div className="text-2xl font-bold text-blue-700">{selectedQuote.distance_km || '-'}</div>
                    <div className="text-sm text-blue-600">km</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <div className="text-2xl font-bold text-slate-700">{selectedQuote.estimated_duration || '-'}</div>
                    <div className="text-sm text-slate-600">durée</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-xl text-center">
                    <div className="text-2xl font-bold text-orange-700">{selectedQuote.suggested_vehicle || '-'}</div>
                    <div className="text-sm text-orange-600">véhicule</div>
                  </div>
                </div>

                {/* Price */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Tarification
                  </h3>
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-sm text-slate-500">Prix estimé:</span>
                      <p className="text-lg font-semibold">{selectedQuote.estimated_price || '-'}€</p>
                    </div>
                    <div className="flex-1">
                      <span className="text-sm text-slate-500">Prix final:</span>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="number"
                          placeholder="Prix final"
                          defaultValue={selectedQuote.final_price || ''}
                          onBlur={(e) => updateFinalPrice(selectedQuote.id, e.target.value)}
                          className="w-32"
                        />
                        <span className="self-center">€</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => downloadPDF(selectedQuote.id)}
                    className="flex-1"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger PDF
                  </Button>
                  {selectedQuote.status === 'pending' && (
                    <Button
                      onClick={() => updateQuoteStatus(selectedQuote.id, 'validated')}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Valider
                    </Button>
                  )}
                  {selectedQuote.status === 'validated' && (
                    <Button
                      onClick={() => updateQuoteStatus(selectedQuote.id, 'sent')}
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Marquer envoyé
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminQuotes;
